import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Plane, 
  Sun, 
  Moon, 
  Mail,
  Send,
  Loader2,
  MapPin,
  Phone,
  Clock
} from "lucide-react";

const Contact = () => {
  const { theme, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Compila tutti i campi");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Messaggio inviato con successo!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Errore nell'invio del messaggio");
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email",
      value: "supporto@travelpro.it",
      href: "mailto:supporto@travelpro.it"
    },
    {
      icon: <Phone className="w-5 h-5" />,
      title: "Telefono",
      value: "+39 02 1234567",
      href: "tel:+390212345670"
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: "Indirizzo",
      value: "Milano, Italia",
      href: null
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Orari",
      value: "Lun-Ven, 9:00-18:00",
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
              <Plane className="w-6 h-6 text-primary" />
              <span className="font-heading text-lg font-bold">TravelPro</span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/contact" className="text-sm font-medium text-primary">
                Contatti
              </Link>
            </div>

            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="contact-theme-toggle">
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Contattaci</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Hai domande o suggerimenti? Siamo qui per aiutarti. 
            Compila il modulo e ti risponderemo al più presto.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-4">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      {info.icon}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{info.title}</p>
                      {info.href ? (
                        <a 
                          href={info.href} 
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Invia un messaggio</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nome *</Label>
                      <Input 
                        id="name"
                        placeholder="Il tuo nome"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        placeholder="la-tua@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Oggetto *</Label>
                    <Input 
                      id="subject"
                      placeholder="Di cosa hai bisogno?"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Messaggio *</Label>
                    <Textarea 
                      id="message"
                      placeholder="Scrivi il tuo messaggio..."
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full sm:w-auto rounded-full px-8"
                    disabled={sending}
                    data-testid="contact-submit-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invia messaggio
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Plane className="w-5 h-5 text-primary" />
            <span className="font-heading font-semibold">TravelPro</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2024 TravelPro. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
